import { formatCurrency } from '@bitstopco/bitstop-theme/helpers';

import api from '@/store/api';

import type { PrecioResponse } from '@/types';

/**
 * Precio RTK-Query API
 * For more information about RTK-Query see
 * * https://redux-toolkit.js.org/rtk-query/overview
 * ! For the precio webhook implementation we are using the custom query method `queryfn`
 * ! for more information see
 * ! https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#customizing-queries-with-queryfn
 */
// If the price feed doesn't open within this window (unreachable host, restrictive
// network), give up instead of leaving a half-open socket for the life of the page.
const CONNECT_TIMEOUT_MS = 8000;

export const precioApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPrecio: build.query<PrecioResponse, void>({
      //Return default precio value
      queryFn: () => ({ data: { value: 0, formattedValue: '' } }),
      onCacheEntryAdded: async (_, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) => {
        const wssUri = import.meta.env.VITE_PRECIO_WSS_URI;

        // No price feed configured (e.g. local/test environments) -- keep the default value.
        if (!wssUri) {
          await cacheEntryRemoved;
          return;
        }

        let ws: WebSocket;
        try {
          // create a websocket connection when the cache subscription starts
          ws = new WebSocket(wssUri);
        } catch {
          // Malformed URL -- there's nothing to connect to or clean up.
          await cacheEntryRemoved;
          return;
        }

        const connectTimeout = setTimeout(() => {
          if (ws.readyState === WebSocket.CONNECTING) {
            ws.close();
          }
        }, CONNECT_TIMEOUT_MS);

        ws.addEventListener('open', () => clearTimeout(connectTimeout));

        try {
          // wait for the initial query to resolve before proceeding
          await cacheDataLoaded;

          const listener = (event: MessageEvent) => {
            const data = JSON.parse(event.data);
            const value = data?.C_RAW || 0;
            const formattedValue = formatCurrency(value, '');

            updateCachedData((draft) => {
              draft.value = value;
              draft.formattedValue = formattedValue;
            });
          };

          ws.addEventListener('message', listener);
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
          // in which case `cacheDataLoaded` will throw
        }

        // cacheEntryRemoved will resolve when the cache subscription is no longer active
        await cacheEntryRemoved;

        // perform cleanup steps once the `cacheEntryRemoved` promise resolves
        clearTimeout(connectTimeout);
        ws.close();
      },
    }),
  }),
});

export const { useGetPrecioQuery } = precioApi;

export const {
  endpoints: { getPrecio },
} = precioApi;
